import React, { useRef, useState, useEffect } from 'react';
import { styles } from '../../../utils/styles';
import DeltaSwitch from 'Components/GMS/Products/ProductView/DeltaSwitch';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import { FieldConfigLabelSection } from './components/FieldConfigLabelSection';
import { FieldConfigStyleTypeSection } from './components/FieldConfigStyleTypeSection';
import { FieldConfigShowTitleSection } from './components/FieldConfigShowTitleSection';
import { FieldConfigExclusionsSection } from './components/FieldConfigExclusionsSection';
import { DropdownIcon } from '../CategoryConfig/components/DropdownIcon';

interface MultiChoiceFieldConfigProps {
  id?: string;
  fieldName?: string;
  label: string;
  onLabelChange: (value: string) => void;
  exclusionType: 'hide' | 'disable';
  onExclusionChange: (type: 'hide' | 'disable') => void;
  showTitle?: boolean;
  onShowTitleChange?: (value: boolean) => void;
  displayName?: string;
  onDisplayNameChange?: (value: string) => void;
  style: 'Chip' | 'Checkbox' | 'Radio Button' | 'Dropdown';
  onStyleChange: (style: string) => void;
  chipSize?: 'Medium' | 'Large';
  onChipSizeChange?: (size: string) => void;
  showImage?: boolean;
  onShowImageChange?: (show: boolean) => void;
  imageDisplay?: 'Fit' | 'Fill' | 'Stretch';
  onImageDisplayChange?: (display: string) => void;
  layoutStyle?: 'Standard' | 'Padded Frame';
  onLayoutStyleChange?: (style: string) => void;
  captionAlignment?: 'Left' | 'Centre' | 'Right';
  onCaptionAlignmentChange?: (alignment: string) => void;
  columnSelection?: 'Auto' | '1' | '2' | '3' | '4';
  onColumnSelectionChange?: (columns: string) => void;
  fillDirection?: 'Horizontal' | 'Vertical';
  onFillDirectionChange?: (direction: string) => void;
  layoutDirection?: 'Horizontal' | 'Vertical';
  onLayoutDirectionChange?: (direction: string) => void;
  chipWidth?: 'Fixed' | 'Dynamic';
  onChipWidthChange?: (width: string) => void;
  chipLabelPosition?: 'Right side of image' | 'Below Image' | 'Show only on hover' | 'Hide label';
  onChipLabelPositionChange?: (position: string) => void;
  showSearchBar?: boolean;
  onShowSearchBarChange?: (show: boolean) => void;
  showExpandCollapse?: boolean;
  onShowExpandCollapseChange?: (show: boolean) => void;
  // Load Options props
  loadOption?: 'All' | 'Partial';
  onLoadOptionChange?: (option: 'All' | 'Partial') => void;
  loadMoreButtonLabel?: string;
  loadLessButtonLabel?: string;
  onLoadMoreButtonLabelChange?: (label: string) => void;
  onLoadLessButtonLabelChange?: (label: string) => void;
  initialLoadCount?: number;
  onInitialLoadCountChange?: (count: number) => void;
  loadRemainingOption?: 'Show All' | 'In Batch';
  onLoadRemainingOptionChange?: (option: 'Show All' | 'In Batch') => void;
  batchCount?: number;
  onBatchCountChange?: (count: number) => void;
  // Excluded options prop
  excludedOption?: 'Hide' | 'Disable' | 'Disable and moved to the bottom of the list';
  onExcludedOptionChange?: (option: 'Hide' | 'Disable' | 'Disable and moved to the bottom of the list') => void;
}

const MultiChoiceFieldConfig = ({
  id,
  fieldName = 'MultiChoice Field',
  label,
  onLabelChange,
  exclusionType,
  onExclusionChange,
  showTitle = true,
  onShowTitleChange,
  displayName = '',
  onDisplayNameChange,
  style,
  onStyleChange,
  chipSize = 'Medium',
  onChipSizeChange,
  showImage = false,
  onShowImageChange,
  imageDisplay = 'Fit',
  onImageDisplayChange,
  layoutStyle = 'Standard',
  onLayoutStyleChange,
  captionAlignment = 'Centre',
  onCaptionAlignmentChange,
  columnSelection = 'Auto',
  onColumnSelectionChange,
  fillDirection = 'Vertical',
  onFillDirectionChange,
  layoutDirection = 'Horizontal',
  onLayoutDirectionChange,
  chipWidth = 'Fixed',
  onChipWidthChange,
  chipLabelPosition = 'Below Image',
  onChipLabelPositionChange,
  showSearchBar = false,
  onShowSearchBarChange,
  showExpandCollapse = false,
  onShowExpandCollapseChange,
  // Load Options props with defaults
  loadOption = 'All',
  onLoadOptionChange,
  loadMoreButtonLabel = 'Show More',
  loadLessButtonLabel = 'Show Less',
  onLoadMoreButtonLabelChange,
  onLoadLessButtonLabelChange,
  initialLoadCount = 5,
  onInitialLoadCountChange,
  loadRemainingOption = 'Show All',
  onLoadRemainingOptionChange,
  batchCount = 5,
  onBatchCountChange,
  // Excluded options prop with default
  excludedOption = 'Disable',
  onExcludedOptionChange
} : MultiChoiceFieldConfigProps) => {
  const baseId = id || 'multichoice-field-config';

  const showLayoutDirection = style !== 'Dropdown';

  // Debouncing infrastructure for text inputs
  const loadMoreLabelDebounceRef = useRef<NodeJS.Timeout>();
  const loadLessLabelDebounceRef = useRef<NodeJS.Timeout>();
  const initialLoadCountDebounceRef = useRef<NodeJS.Timeout>();
  const batchCountDebounceRef = useRef<NodeJS.Timeout>();

  const isEditingInitialLoadCountRef = useRef<boolean>(false);
  const isEditingBatchCountRef = useRef<boolean>(false);

  const [localLoadMoreButtonLabel, setLocalLoadMoreButtonLabel] = useState<string>(loadMoreButtonLabel || 'Show More');
  const [localLoadLessButtonLabel, setLocalLoadLessButtonLabel] = useState<string>(loadLessButtonLabel || 'Show Less');

  const [localInitialLoadCount, setLocalInitialLoadCount] = useState<string>((initialLoadCount || 5).toString());
  const [localBatchCount, setLocalBatchCount] = useState<string>((batchCount || 5).toString());

  useEffect(() => {
    setLocalLoadMoreButtonLabel(loadMoreButtonLabel || 'Show More');
  }, [loadMoreButtonLabel]);

  useEffect(() => {
    setLocalLoadLessButtonLabel(loadLessButtonLabel || 'Show Less');
  }, [loadLessButtonLabel]);

  useEffect(() => {
    if (!isEditingInitialLoadCountRef.current) {
      const newValue = initialLoadCount || 5;
      setLocalInitialLoadCount(newValue.toString());
    }
  }, [initialLoadCount]);

  useEffect(() => {
    if (!isEditingBatchCountRef.current) {
      const newValue = batchCount || 5;
      setLocalBatchCount(newValue.toString());
    }
  }, [batchCount]);

  useEffect(() => {
    return () => {
      if (loadMoreLabelDebounceRef.current) clearTimeout(loadMoreLabelDebounceRef.current);
      if (loadLessLabelDebounceRef.current) clearTimeout(loadLessLabelDebounceRef.current);
      if (initialLoadCountDebounceRef.current) clearTimeout(initialLoadCountDebounceRef.current);
      if (batchCountDebounceRef.current) clearTimeout(batchCountDebounceRef.current);
    };
  }, []);

  const handleDebouncedLoadMoreLabelChange = (value: string) => {
    setLocalLoadMoreButtonLabel(value);
    if (loadMoreLabelDebounceRef.current) {
      clearTimeout(loadMoreLabelDebounceRef.current);
    }
    loadMoreLabelDebounceRef.current = setTimeout(() => {
      onLoadMoreButtonLabelChange && onLoadMoreButtonLabelChange(value);
    }, 500);
  };

  const handleDebouncedLoadLessLabelChange = (value: string) => {
    setLocalLoadLessButtonLabel(value);
    if (loadLessLabelDebounceRef.current) {
      clearTimeout(loadLessLabelDebounceRef.current);
    }
    loadLessLabelDebounceRef.current = setTimeout(() => {
      onLoadLessButtonLabelChange && onLoadLessButtonLabelChange(value);
    }, 500);
  };

  const handleDebouncedInitialLoadCountChange = (value: number) => {
    setLocalInitialLoadCount(value.toString());
    if (initialLoadCountDebounceRef.current) {
      clearTimeout(initialLoadCountDebounceRef.current);
    }
    initialLoadCountDebounceRef.current = setTimeout(() => {
      onInitialLoadCountChange && onInitialLoadCountChange(value);
    }, 500);
  };

  const handleDebouncedBatchCountChange = (value: number) => {
    setLocalBatchCount(value.toString());
    if (batchCountDebounceRef.current) {
      clearTimeout(batchCountDebounceRef.current);
    }
    batchCountDebounceRef.current = setTimeout(() => {
      onBatchCountChange && onBatchCountChange(value);
    }, 500);
  };
  
  const renderStyleSpecificConfig = () => {
    switch (style) {
      case 'Chip':
        return (
          <>
            <div id={`${baseId}-chip-size-section`} className="tw-mb-4">
              <label id={`${baseId}-chip-size-label`} className={styles.inputLabel}>Chip Size</label>
              <div id={`${baseId}-chip-size-select-container`} className={styles.selectContainer}>
                <select
                  id={`${baseId}-chip-size-select`}
                  data-testid={`${baseId}-chip-size-select`}
                  value={chipSize}
                  onChange={(e) => onChipSizeChange && onChipSizeChange(e.target.value)}
                  className={styles.select}
                >
                  <option value="Medium">Medium</option>
                  <option value="Large">Large</option>
                </select>
                <div id={`${baseId}-chip-size-select-icon`} className={styles.selectIcon}>
                  <DropdownIcon />
                </div>
              </div>
            </div>

            <div id={`${baseId}-show-image-section`} className="tw-mb-4">
              <label id={`${baseId}-show-image-label`} className={styles.checkboxContainer}>
                <input
                  id={`${baseId}-show-image-checkbox`}
                  data-testid={`${baseId}-show-image-checkbox`}
                  type="checkbox"
                  checked={showImage}
                  onChange={(e) => onShowImageChange && onShowImageChange(e.target.checked)}
                  className={styles.checkboxInput}
                />
                <span id={`${baseId}-show-image-text`} className={styles.checkboxLabel}>Show Image</span>
              </label>
            </div>

            {showImage && (
              <>
                <div id={`${baseId}-image-display-section`} className="tw-mb-4 tw-ml-6">
                  <div id={`${baseId}-image-display-header`} className="tw-flex tw-items-center tw-gap-2">
                    <label id={`${baseId}-image-display-label`} className={styles.inputLabel}>Image Display</label>
                      <OverlayIcon message={''} />

                  </div>
                  <div id={`${baseId}-image-display-select-container`} className={styles.selectContainer}>
                    <select
                      id={`${baseId}-image-display-select`}
                      data-testid={`${baseId}-image-display-select`}
                      value={imageDisplay}
                      onChange={(e) => onImageDisplayChange && onImageDisplayChange(e.target.value)}
                      className={styles.select}
                    >
                      <option value="Fit">Fit</option>
                      <option value="Fill">Fill</option>
                      <option value="Stretch">Stretch</option>
                    </select>
                    <div id={`${baseId}-image-display-select-icon`} className={styles.selectIcon}>
                      <DropdownIcon />
                    </div>
                  </div>
                </div>

                {chipSize === 'Large' && (
                  <>
                    <div id={`${baseId}-layout-style-section`} className="tw-mb-4 tw-ml-6">
                      <div id={`${baseId}-layout-style-header`} className="tw-flex tw-items-center tw-gap-2 ">
                        <label id={`${baseId}-layout-style-label`} className={styles.inputLabel}>Layout Style</label>
                        <OverlayIcon message={''} />
                      </div>
                      <div id={`${baseId}-layout-style-select-container`} className={styles.selectContainer}>
                        <select
                          id={`${baseId}-layout-style-select`}
                          data-testid={`${baseId}-layout-style-select`}
                          value={layoutStyle}
                          onChange={(e) => onLayoutStyleChange && onLayoutStyleChange(e.target.value)}
                          className={styles.select}
                        >
                          <option value="Standard">Standard</option>
                          <option value="Padded Frame">Padded Frame</option>
                        </select>
                        <div id={`${baseId}-layout-style-select-icon`} className={styles.selectIcon}>
                          <DropdownIcon />
                        </div>
                      </div>
                    </div>

                    <div id={`${baseId}-chip-label-position-large-section`} className="tw-mb-4 tw-ml-6">
                      <label id={`${baseId}-chip-label-position-large-label`} className={styles.inputLabel}>Chip Label position</label>
                      <div id={`${baseId}-chip-label-position-large-options`} className="tw-flex tw-flex-col tw-gap-2">
                        <label id={`${baseId}-chip-label-position-large-below-label`} className="tw-flex tw-items-center">
                          <input
                            id={`${baseId}-chip-label-position-large-below-radio`}
                            data-testid={`${baseId}-chip-label-position-large-below-radio`}
                            type="radio"
                            name="chipLabelPositionLarge"
                            value="Below Image"
                            checked={chipLabelPosition === 'Below Image'}
                            onChange={() => onChipLabelPositionChange && onChipLabelPositionChange('Below Image')}
                            className="tw-mr-2 tw-accent-[#091CAA]"
                          />
                          <span id={`${baseId}-chip-label-position-large-below-text`} className="tw-text-sm">Below Image</span>
                        </label>
                        <label id={`${baseId}-chip-label-position-large-hover-label`} className="tw-flex tw-items-center">
                          <input
                            id={`${baseId}-chip-label-position-large-hover-radio`}
                            data-testid={`${baseId}-chip-label-position-large-hover-radio`}
                            type="radio"
                            name="chipLabelPositionLarge"
                            value="Show only on hover"
                            checked={chipLabelPosition === 'Show only on hover'}
                            onChange={() => onChipLabelPositionChange && onChipLabelPositionChange('Show only on hover')}
                            className="tw-mr-2 tw-accent-[#091CAA]"
                          />
                          <span id={`${baseId}-chip-label-position-large-hover-text`} className="tw-text-sm">Show only on hover</span>
                        </label>
                        <label id={`${baseId}-chip-label-position-large-hide-label`} className="tw-flex tw-items-center">
                          <input
                            id={`${baseId}-chip-label-position-large-hide-radio`}
                            data-testid={`${baseId}-chip-label-position-large-hide-radio`}
                            type="radio"
                            name="chipLabelPositionLarge"
                            value="Hide label"
                            checked={chipLabelPosition === 'Hide label'}
                            onChange={() => onChipLabelPositionChange && onChipLabelPositionChange('Hide label')}
                            className="tw-mr-2 tw-accent-[#091CAA]"
                          />
                          <span id={`${baseId}-chip-label-position-large-hide-text`} className="tw-text-sm">Hide label</span>
                        </label>
                      </div>
                    </div>

                    <div id={`${baseId}-chip-label-alignment-section`} className="tw-mb-4 tw-ml-6">
                      <label id={`${baseId}-chip-label-alignment-label`} className={styles.inputLabel}>Chip Label Alignment</label>
                      <DeltaSwitch
                        labels={['Left', 'Centre', 'Right']}
                        id={`${baseId}-chip-label-alignment-switch`}
                        defaultSelectedIndex={['Left', 'Centre', 'Right'].indexOf(captionAlignment)}
                        onSelectionChange={(selected) => onCaptionAlignmentChange && onCaptionAlignmentChange(selected)}
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {/* Chip Label position - Only for Medium chips with images */}
            {chipSize === 'Medium' && showImage && (
              <div id={`${baseId}-chip-label-position-medium-section`} className="tw-mb-4 tw-ml-6">
                <label id={`${baseId}-chip-label-position-medium-label`} className={styles.inputLabel}>Chip Label position</label>
                <div id={`${baseId}-chip-label-position-medium-options`} className="tw-flex tw-flex-col tw-gap-2">
                  <label id={`${baseId}-chip-label-position-medium-right-label`} className="tw-flex tw-items-center">
                    <input
                      id={`${baseId}-chip-label-position-medium-right-radio`}
                      data-testid={`${baseId}-chip-label-position-medium-right-radio`}
                      type="radio"
                      name="chipLabelPosition"
                      value="Right side of image"
                      checked={chipLabelPosition === 'Right side of image'}
                      onChange={() => onChipLabelPositionChange && onChipLabelPositionChange('Right side of image')}
                      className="tw-mr-2 tw-accent-[#091CAA]"
                    />
                    <span id={`${baseId}-chip-label-position-medium-right-text`} className="tw-text-sm">Right side of image</span>
                  </label>
                  <label id={`${baseId}-chip-label-position-medium-below-label`} className="tw-flex tw-items-center">
                    <input
                      id={`${baseId}-chip-label-position-medium-below-radio`}
                      data-testid={`${baseId}-chip-label-position-medium-below-radio`}
                      type="radio"
                      name="chipLabelPosition"
                      value="Below Image"
                      checked={chipLabelPosition === 'Below Image'}
                      onChange={() => onChipLabelPositionChange && onChipLabelPositionChange('Below Image')}
                      className="tw-mr-2 tw-accent-[#091CAA]"
                    />
                    <span id={`${baseId}-chip-label-position-medium-below-text`} className="tw-text-sm">Below Image</span>
                  </label>
                  <label id={`${baseId}-chip-label-position-medium-hover-label`} className="tw-flex tw-items-center">
                    <input
                      id={`${baseId}-chip-label-position-medium-hover-radio`}
                      data-testid={`${baseId}-chip-label-position-medium-hover-radio`}
                      type="radio"
                      name="chipLabelPosition"
                      value="Show only on hover"
                      checked={chipLabelPosition === 'Show only on hover'}
                      onChange={() => onChipLabelPositionChange && onChipLabelPositionChange('Show only on hover')}
                      className="tw-mr-2 tw-accent-[#091CAA]"
                    />
                    <span id={`${baseId}-chip-label-position-medium-hover-text`} className="tw-text-sm">Show only on hover</span>
                  </label>
                  <label id={`${baseId}-chip-label-position-medium-hide-label`} className="tw-flex tw-items-center">
                    <input
                      id={`${baseId}-chip-label-position-medium-hide-radio`}
                      data-testid={`${baseId}-chip-label-position-medium-hide-radio`}
                      type="radio"
                      name="chipLabelPosition"
                      value="Hide label"
                      checked={chipLabelPosition === 'Hide label'}
                      onChange={() => onChipLabelPositionChange && onChipLabelPositionChange('Hide label')}
                      className="tw-mr-2 tw-accent-[#091CAA]"
                    />
                    <span id={`${baseId}-chip-label-position-medium-hide-text`} className="tw-text-sm">Hide label</span>
                  </label>
                </div>
              </div>
            )}

            <div id={`${baseId}-chip-width-section`} className="tw-mb-4">
            <label id={`${baseId}-chip-width-label`} className={styles.inputLabel}>Chip Width</label>
            <div id={`${baseId}-chip-width-options`} className="tw-flex tw-items-center tw-space-x-4">
              <label id={`${baseId}-chip-width-fixed-label`} className="tw-flex tw-items-center">
                <input
                  id={`${baseId}-chip-width-fixed-radio`}
                  data-testid={`${baseId}-chip-width-fixed-radio`}
                  type="radio"
                  name="chipWidth"
                  value="Fixed"
                  checked={chipWidth === 'Fixed'}
                  onChange={() => onChipWidthChange && onChipWidthChange('Fixed')}
                  className="tw-mr-2 tw-accent-[#091CAA]"
                />
                <span id={`${baseId}-chip-width-fixed-text`} className="tw-text-sm">Fixed</span>
              </label>
              <label id={`${baseId}-chip-width-dynamic-label`} className="tw-flex tw-items-center">
                <input
                  id={`${baseId}-chip-width-dynamic-radio`}
                  data-testid={`${baseId}-chip-width-dynamic-radio`}
                  type="radio"
                  name="chipWidth"
                  value="Dynamic"
                  checked={chipWidth === 'Dynamic'}
                  onChange={() => onChipWidthChange && onChipWidthChange('Dynamic')}
                  className="tw-mr-2 tw-accent-[#091CAA]"
                />
                <span id={`${baseId}-chip-width-dynamic-text`} className="tw-text-sm">Dynamic</span>
              </label>
            </div>
          </div>
          </>
        );

      // case 'Checkbox':
      //   return (
      //     <>
      //       <div id={`${baseId}-column-selection-section`} className="tw-mb-4">
      //         <label id={`${baseId}-column-selection-label`} className={styles.inputLabel}>Column Selection</label>
      //         <div id={`${baseId}-column-selection-select-container`} className={styles.selectContainer}>
      //           <select
      //             id={`${baseId}-column-selection-select`}
      //             data-testid={`${baseId}-column-selection-select`}
      //             value={columnSelection}
      //             onChange={(e) => onColumnSelectionChange && onColumnSelectionChange(e.target.value)}
      //             className={styles.select}
      //           >
      //             <option value="Auto">Auto</option>
      //             <option value="1">1</option>
      //             <option value="2">2</option>
      //             <option value="3">3</option>
      //             <option value="4">4</option>
      //           </select>
      //           <div id={`${baseId}-column-selection-select-icon`} className={styles.selectIcon}>
      //             <DropdownIcon />
      //           </div>
      //         </div>
      //       </div>
      //     </>
      //   );
      
      case 'Radio Button':
      case 'Dropdown':
        return null;
      
      default:
        return null;
    }
  };

  return (
    <div id={baseId} data-testid={baseId}>
      <FieldConfigLabelSection id={`${baseId}-label-section`} label={fieldName || label} placeholderSuffix="Field name" />
      <FieldConfigStyleTypeSection id={`${baseId}-style-type-section`} fieldTypeName="MultiChoice Field" />
      <FieldConfigShowTitleSection
        id={`${baseId}-show-title-section`}
        showTitle={showTitle}
        onShowTitleChange={onShowTitleChange}
        displayName={displayName}
        onDisplayNameChange={onDisplayNameChange}
      />

      {/* Style selection dropdown */}
      <div id={`${baseId}-style-section`} className="tw-mb-4">
        <label id={`${baseId}-style-label`} className={styles.inputLabel}>Style</label>
        <div id={`${baseId}-style-select-container`} className={styles.selectContainer}>
          <select
            id={`${baseId}-style-select`}
            data-testid={`${baseId}-style-select`}
            value={style}
            onChange={(e) => onStyleChange(e.target.value)}
            className={styles.select}
          >
            <option value="Chip">Chip</option>
            <option value="Checkbox">Checkbox</option>
            <option value="Radio Button">Radio Button</option>
            <option value="Dropdown">Dropdown</option>
          </select>
          <div id={`${baseId}-style-select-icon`} className={styles.selectIcon}>
            <DropdownIcon />
          </div>
        </div>
      </div>
      
      {/* Style specific configuration */}
      {renderStyleSpecificConfig()}
      
      {/* Load Options Section */}
      <div id={`${baseId}-load-options-section`} className="tw-mb-4">
        <label id={`${baseId}-load-options-label`} className={styles.inputLabel}>Load Options</label>
        <div id={`${baseId}-load-options-radios`} className="tw-flex tw-items-center tw-space-x-4">
          <label id={`${baseId}-load-options-all-label`} className="tw-flex tw-items-center">
            <input
              id={`${baseId}-load-options-all-radio`}
              data-testid={`${baseId}-load-options-all-radio`}
              type="radio"
              name="loadOption"
              value="All"
              checked={loadOption === 'All'}
              onChange={() => onLoadOptionChange && onLoadOptionChange('All')}
              className="tw-mr-2 tw-accent-[#091CAA]"
            />
            <span id={`${baseId}-load-options-all-text`} className="tw-text-sm">All</span>
          </label>
          <label id={`${baseId}-load-options-partial-label`} className="tw-flex tw-items-center">
            <input
              id={`${baseId}-load-options-partial-radio`}
              data-testid={`${baseId}-load-options-partial-radio`}
              type="radio"
              name="loadOption"
              value="Partial"
              checked={loadOption === 'Partial'}
              onChange={() => onLoadOptionChange && onLoadOptionChange('Partial')}
              className="tw-mr-2 tw-accent-[#091CAA]"
            />
            <span id={`${baseId}-load-options-partial-text`} className="tw-text-sm">Partial</span>
          </label>
        </div>
        
        {/* Partial Load Options - Only show when Partial is selected */}
        {loadOption === 'Partial' && (
          <div id={`${baseId}-partial-options-wrapper`} className="tw-mt-3 tw-space-y-4">
            {/* Button Label */}
            <div id={`${baseId}-load-more-button-label-section`} className="tw-ml-6">
              <label id={`${baseId}-load-more-button-label-label`} className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                Button Label
              </label>
              <input
                id={`${baseId}-load-more-button-label-input`}
                data-testid={`${baseId}-load-more-button-label-input`}
                type="text"
                value={localLoadMoreButtonLabel}
                onChange={(e) => handleDebouncedLoadMoreLabelChange(e.target.value)}
                className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2 tw-bg-white"
                placeholder="Enter button label"
              />
            </div>
            {/* Button Label */}
            <div id={`${baseId}-load-less-button-label-section`} className="tw-ml-6">
              <label id={`${baseId}-load-less-button-label-label`} className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                Show Less Button Label
              </label>
              <input
                id={`${baseId}-load-less-button-label-input`}
                data-testid={`${baseId}-load-less-button-label-input`}
                type="text"
                value={localLoadLessButtonLabel}
                onChange={(e) => handleDebouncedLoadLessLabelChange(e.target.value)}
                className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2 tw-bg-white"
                placeholder="Enter button label"
              />
            </div>
            
            {/* Initial Load Count */}
            <div id={`${baseId}-initial-load-count-section`} className="tw-ml-6">
              <div id={`${baseId}-initial-load-count-header`} className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                <label id={`${baseId}-initial-load-count-label`} className="tw-text-[#6D788C] tw-text-sm">
                  Initial Load Count
                </label>
                <OverlayIcon message="Number of items to show initially" />
              </div>
              <input
                id={`${baseId}-initial-load-count-input`}
                data-testid={`${baseId}-initial-load-count-input`}
                type="text"
                inputMode="numeric"
                value={localInitialLoadCount}
                onFocus={() => {
                  // FIX for bug #909: Mark that user is actively editing this field
                  isEditingInitialLoadCountRef.current = true;
                }}
                onChange={(e) => {
                  // FIX for bug #909: Track editing state to prevent useEffect from overwriting
                  isEditingInitialLoadCountRef.current = true;

                  const inputValue = e.target.value;

                  // Allow empty string during typing - user can delete completely
                  if (inputValue === '') {
                    setLocalInitialLoadCount('');
                    return;
                  }

                  // Only allow numeric input
                  if (!/^\d+$/.test(inputValue)) {
                    return; // Reject non-numeric input
                  }

                  // Update state as string
                  setLocalInitialLoadCount(inputValue);

                  // Parse and send to parent (convert to number)
                  const value = parseInt(inputValue, 10);
                  if (value >= 1) {
                    handleDebouncedInitialLoadCountChange(value);
                  }
                }}
                onBlur={(e) => {
                  // FIX for bug #909: Mark that user is no longer editing, allow useEffect sync again
                  isEditingInitialLoadCountRef.current = false;

                  // Cancel pending debounced callback
                  if (initialLoadCountDebounceRef.current) {
                    clearTimeout(initialLoadCountDebounceRef.current);
                    initialLoadCountDebounceRef.current = undefined;
                  }

                  const inputValue = localInitialLoadCount;
                  // FIX for bug #909: Default to 5 for empty or invalid input, convert to number for parent
                  if (inputValue === '' || !/^\d+$/.test(inputValue)) {
                    const finalValue = 5;
                    setLocalInitialLoadCount(finalValue.toString());
                    onInitialLoadCountChange && onInitialLoadCountChange(finalValue);
                  } else {
                    const value = parseInt(inputValue, 10);
                    const finalValue = value < 1 ? 5 : value;
                    setLocalInitialLoadCount(finalValue.toString());
                    onInitialLoadCountChange && onInitialLoadCountChange(finalValue);
                  }
                }}
                className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2 tw-bg-white"
                placeholder="Enter count"
              />
            </div>

            {/* Load Remaining Count */}
            <div id={`${baseId}-load-remaining-count-section`} className="tw-ml-6">
              <div id={`${baseId}-load-remaining-count-header`} className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                <label id={`${baseId}-load-remaining-count-label`} className="tw-text-[#6D788C] tw-text-sm">
                  Load Remaining Count
                </label>
                <OverlayIcon message="How to load the remaining items" />
              </div>
              <div id={`${baseId}-load-remaining-count-radios`} className="tw-flex tw-items-center tw-space-x-4">
                <label id={`${baseId}-load-remaining-all-label`} className="tw-flex tw-items-center">
                  <input
                    id={`${baseId}-load-remaining-all-radio`}
                    data-testid={`${baseId}-load-remaining-all-radio`}
                    type="radio"
                    name="loadRemainingOption"
                    value="Show All"
                    checked={loadRemainingOption === 'Show All'}
                    onChange={() => onLoadRemainingOptionChange && onLoadRemainingOptionChange('Show All')}
                    className="tw-mr-2 tw-accent-[#091CAA]"
                  />
                  <span id={`${baseId}-load-remaining-all-text`} className="tw-text-sm">Show All</span>
                </label>
                <label id={`${baseId}-load-remaining-batch-label`} className="tw-flex tw-items-center">
                  <input
                    id={`${baseId}-load-remaining-batch-radio`}
                    data-testid={`${baseId}-load-remaining-batch-radio`}
                    type="radio"
                    name="loadRemainingOption"
                    value="In Batch"
                    checked={loadRemainingOption === 'In Batch'}
                    onChange={() => onLoadRemainingOptionChange && onLoadRemainingOptionChange('In Batch')}
                    className="tw-mr-2 tw-accent-[#091CAA]"
                  />
                  <span id={`${baseId}-load-remaining-batch-text`} className="tw-text-sm">In Batch</span>
                </label>
              </div>
            </div>
            
            {/* Batch Count - Only show when In Batch is selected */}
            {loadRemainingOption === 'In Batch' && (
              <div id={`${baseId}-batch-count-section`} className="tw-ml-6 tw-mt-3">
                <div id={`${baseId}-batch-count-header`} className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <label id={`${baseId}-batch-count-label`} className="tw-text-[#6D788C] tw-text-sm">
                    Batch Count
                  </label>
                  <OverlayIcon message="Number of items to load per batch" />
                </div>
                <input
                  id={`${baseId}-batch-count-input`}
                  data-testid={`${baseId}-batch-count-input`}
                  type="text"
                  inputMode="numeric"
                  value={localBatchCount}
                  onFocus={() => {
                    // FIX for bug #909: Mark that user is actively editing this field
                    isEditingBatchCountRef.current = true;
                  }}
                  onChange={(e) => {
                    // FIX for bug #909: Track editing state to prevent useEffect from overwriting
                    isEditingBatchCountRef.current = true;

                    const inputValue = e.target.value;

                    // Allow empty string during typing - user can delete completely
                    if (inputValue === '') {
                      setLocalBatchCount('');
                      return;
                    }

                    // Only allow numeric input
                    if (!/^\d+$/.test(inputValue)) {
                      return; // Reject non-numeric input
                    }

                    // Update state as string
                    setLocalBatchCount(inputValue);

                    // Parse and send to parent (convert to number)
                    const value = parseInt(inputValue, 10);
                    if (value >= 1) {
                      handleDebouncedBatchCountChange(value);
                    }
                  }}
                  onBlur={(e) => {
                    // FIX for bug #909: Mark that user is no longer editing, allow useEffect sync again
                    isEditingBatchCountRef.current = false;

                    // Cancel pending debounced callback
                    if (batchCountDebounceRef.current) {
                      clearTimeout(batchCountDebounceRef.current);
                      batchCountDebounceRef.current = undefined;
                    }

                    const inputValue = localBatchCount;
                    // FIX for bug #909: Default to 5 for empty or invalid input, convert to number for parent
                    if (inputValue === '' || !/^\d+$/.test(inputValue)) {
                      const finalValue = 5;
                      setLocalBatchCount(finalValue.toString());
                      onBatchCountChange && onBatchCountChange(finalValue);
                    } else {
                      const value = parseInt(inputValue, 10);
                      const finalValue = value < 1 ? 5 : value;
                      setLocalBatchCount(finalValue.toString());
                      onBatchCountChange && onBatchCountChange(finalValue);
                    }
                  }}
                  className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2 tw-bg-white"
                  placeholder="Enter batch count"
                />
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Layout Direction - Not shown for Dropdown */}
      {showLayoutDirection && (
        <div id={`${baseId}-layout-direction-section`} className="tw-mb-4">
          <label id={`${baseId}-layout-direction-label`} className={styles.inputLabel}>Layout</label>
          <div id={`${baseId}-layout-direction-radios`} className="tw-flex tw-items-center tw-space-x-4">
            <label id={`${baseId}-layout-direction-horizontal-label`} className="tw-flex tw-items-center">
              <input
                id={`${baseId}-layout-direction-horizontal-radio`}
                data-testid={`${baseId}-layout-direction-horizontal-radio`}
                type="radio"
                name="layout"
                value="Horizontal"
                checked={layoutDirection === 'Horizontal'}
                onChange={() => onLayoutDirectionChange && onLayoutDirectionChange('Horizontal')}
                className="tw-mr-2 tw-accent-[#091CAA]"
              />
              <span id={`${baseId}-layout-direction-horizontal-text`} className="tw-text-sm">Horizontal</span>
            </label>
            <label id={`${baseId}-layout-direction-vertical-label`} className="tw-flex tw-items-center">
              <input
                id={`${baseId}-layout-direction-vertical-radio`}
                data-testid={`${baseId}-layout-direction-vertical-radio`}
                type="radio"
                name="layout"
                value="Vertical"
                checked={layoutDirection === 'Vertical'}
                onChange={() => onLayoutDirectionChange && onLayoutDirectionChange('Vertical')}
                className="tw-mr-2 tw-accent-[#091CAA]"
              />
              <span id={`${baseId}-layout-direction-vertical-text`} className="tw-text-sm">Vertical</span>
            </label>
          </div>
        </div>
      )}

      {showLayoutDirection ? <div id={`${baseId}-search-bar-section`} className="tw-mb-4">
        <label id={`${baseId}-search-bar-label`} className={styles.checkboxContainer}>
          <input
            id={`${baseId}-search-bar-checkbox`}
            data-testid={`${baseId}-search-bar-checkbox`}
            type="checkbox"
            checked={showSearchBar}
            onChange={(e) => onShowSearchBarChange && onShowSearchBarChange(e.target.checked)}
            className={styles.checkboxInput}
          />
          <span id={`${baseId}-search-bar-text`} className={styles.checkboxLabel}>Show 'Search Bar'</span>
        </label>
      </div> : null}


      {showLayoutDirection ? <div id={`${baseId}-expand-collapse-section`} className="tw-mb-4">
        <label id={`${baseId}-expand-collapse-label`} className={styles.checkboxContainer}>
          <input
            id={`${baseId}-expand-collapse-checkbox`}
            data-testid={`${baseId}-expand-collapse-checkbox`}
            type="checkbox"
            checked={showExpandCollapse}
            onChange={(e) => onShowExpandCollapseChange && onShowExpandCollapseChange(e.target.checked)}
            className={styles.checkboxInput}
          />
          <span id={`${baseId}-expand-collapse-text`} className={styles.checkboxLabel}>Show Expand/Collapse</span>
        </label>
      </div> : null }

      <FieldConfigExclusionsSection
        id={`${baseId}-exclusions-section`}
        exclusionType={exclusionType}
        onExclusionChange={onExclusionChange}
      />

      {/* Option exclusions dropdown */}
      <div id={`${baseId}-option-exclusions-section`} className="tw-mb-6">
        <label id={`${baseId}-option-exclusions-label`} className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">Option exclusions</label>
        <div id={`${baseId}-option-exclusions-select-container`} className={styles.selectContainer}>
          <select
            id={`${baseId}-excluded-options-select`}
            data-testid={`${baseId}-excluded-options-select`}
            value={excludedOption}
            onChange={(e) => onExcludedOptionChange && onExcludedOptionChange(e.target.value as 'Hide' | 'Disable' | 'Disable and moved to the bottom of the list')}
            className={styles.select}
          >
            <option value="Hide">Hide</option>
            <option value="Disable">Disable</option>
            <option value="Disable and moved to the bottom of the list">Disable and moved to the bottom of the list</option>
          </select>
          <div id={`${baseId}-excluded-options-select-icon`} className={styles.selectIcon}>
            <DropdownIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiChoiceFieldConfig;
